import { useMemo } from 'react';

import { HistoryContent } from '../types';

type ScheduledContentNode = {
  publishUtc: number | null;
  revisorUser?: { djUserId: string | null } | null;
  body: unknown;
  bodyError?: string;
};

type LastNData = {
  pages: {
    lastNPublishedScheduledContent: {
      edges: { node: ScheduledContentNode }[];
    };
  }[];
};

type NextNData = {
  pages: {
    nextNPublishedScheduledContent: {
      edges: { node: ScheduledContentNode }[];
    };
  }[];
};

type ScheduledData = {
  scheduledContentByAllessehId: { body: unknown }[];
};

interface UsePublishHistoryItemsParams<T extends HistoryContent> {
  liveVersion: T | undefined;
  scheduledContents: ScheduledData | undefined;
  lastNScheduledContentsData: LastNData | undefined;
  lastNFromWindowData: LastNData | undefined;
  nextNFromWindowData: NextNData | undefined;
  windowItems: T[];
}

export const usePublishHistoryItems = <T extends HistoryContent>({
  liveVersion,
  scheduledContents,
  lastNScheduledContentsData,
  lastNFromWindowData,
  nextNFromWindowData,
  windowItems
}: UsePublishHistoryItemsParams<T>): T[] =>
  useMemo(() => {
    if (!liveVersion) return [];

    const isUsableItem = (item: unknown): item is T =>
      item !== null &&
      typeof item === 'object' &&
      typeof (item as { metadata?: { publishUtc?: unknown } }).metadata?.publishUtc === 'number';

    // When a body is null (graphqlClient nulled it due to a serialization error), create a
    // stub using the node's own fields so the entry still appears in the timeline with its
    // timestamp and editor info, while HistoryItem can detect the missing root and show
    // a targeted error message in the content area.
    const edgeToItem = (edge: { node: ScheduledContentNode }): unknown => {
      if (edge.node.body !== null) return edge.node.body;
      if (typeof edge.node.publishUtc !== 'number') return null;
      return {
        metadata: {
          publishUtc: edge.node.publishUtc,
          revisorUser: edge.node.revisorUser ?? undefined,
          bodyError: edge.node.bodyError
        }
      };
    };

    const contentHistory = scheduledContents
      ? scheduledContents.scheduledContentByAllessehId.map((content) => content.body as T)
      : [];
    const lastNScheduledContentMap = lastNScheduledContentsData?.pages
      ? lastNScheduledContentsData.pages
          .flatMap((page) => page.lastNPublishedScheduledContent.edges)
          .map(edgeToItem)
      : [];
    const lastNFromWindowMap = lastNFromWindowData?.pages
      ? lastNFromWindowData.pages
          .flatMap((page) => page.lastNPublishedScheduledContent.edges)
          .map(edgeToItem)
      : [];
    const nextNFromWindowMap = nextNFromWindowData?.pages
      ? nextNFromWindowData.pages
          .flatMap((page) => page.nextNPublishedScheduledContent.edges)
          .map(edgeToItem)
      : [];

    return [
      ...new Map(
        [
          ...lastNScheduledContentMap,
          ...lastNFromWindowMap,
          ...nextNFromWindowMap,
          ...windowItems,
          liveVersion,
          ...contentHistory
        ]
          .filter(isUsableItem)
          .sort((a, b) => (a.metadata.publishUtc ?? 0) - (b.metadata.publishUtc ?? 0))
          .map((item) => [item.metadata.publishUtc, item])
      ).values()
    ];
  }, [
    liveVersion,
    scheduledContents,
    lastNScheduledContentsData,
    lastNFromWindowData,
    nextNFromWindowData,
    windowItems
  ]);
